/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Accordion';
import { helpRenders } from './Accordion_Help';
import { getCustomChildItemTypes } from '../../registry/Manager';

const AccordionManager: ComponentManagerFn<'Accordion'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Accordion'>(platform, 'Accordion'),
    type: 'Accordion',
    name: 'Accordion',
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('Accordion'),
          'BoxContainer',
          'BoxList',
          'CanvasGrid',
          'Card',
          'Calendar',
          'CalendarHeatmap',
          'Chartjs',
          'CombinationChart',
          'Code',
          'DataGrid',
          'DataList',
          'DataStoreReference',
          'DragSource',
          'DropTarget',
          'FlexPanel',
          'Form',
          'FormSearch',
          'HTML',
          'HorizontalSplitPanel',
          'OCRComponent',
          'RowPanel',
          'SearchForm',
          'Toolbar',
          'Typography',
          'VerticalSplitPanel',
          'WorkflowViewer',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      expanded: {
        label: 'Expanded',
        type: 'boolean',
      },
      title: {
        label: 'Title',
        type: 'string',
        required: true,
      },
      subTitle: {
        label: 'Sub Title',
        type: 'string',
      },
      titleBackgroundColor: {
        label: 'Title Background Color',
        type: 'ColorExpression',
      },
      color: {
        label: 'Title Color',
        type: 'ColorExpression',
      },
    },
    getDisplayText: (item) => item.props.title,
    canHaveChildren: () => true,
    category: 'Layout',
    icon: { iconName: 'list' },
    layout: 'sizeAware',
    excludeChildren: true,
    description: `**Overview**<br/>
The Accordion is a Layout component used to organize complex interfaces into collapsible sections. It allows you to group related fields, charts, or data grids under a single header, enabling users to toggle the visibility of the content to save screen real estate and reduce cognitive load.

**1. Configuration**<br/>
The Accordion is defined primarily by its header information and its initial visibility state.
* **Title (Required):** The main text displayed in the clickable header row. This also serves as the identifying name in the designer component tree.
* **Sub Title:** Optional secondary text displayed alongside the main title, useful for providing brief context or status summaries.
* **Expanded:** A boolean property that determines the default state of the section upon page load.
    * **True:** The section loads open, displaying its child content immediately.
    * **False:** The section loads collapsed, showing only the header.

**2. Styling**<br/>
You can customize the visual appearance of the header to create visual hierarchy or highlight specific states.
* **Title Background Color:** Sets the background fill of the header bar.
* **Title Color:** Sets the text color of the Title and Subtitle.
* **Expression Support:** Both color properties support **ColorExpression**, allowing for dynamic styling (e.g., turning the header Red if validation errors are detected in the nested form).

**3. Allowed Content (Children)**<br/>
The Accordion is a highly flexible container that can host nearly any other component type.
* **Layouts:** BoxContainer, FlexPanel, RowPanel, and VerticalSplitPanel.
* **Data & Visuals:** DataGrid, Chartjs, Calendar, and WorkflowViewer.
* **Forms & Text:** Form, SearchForm, and Typography components.
* **Logic:** Buttons, HTML, and custom Code blocks.

**4. Best Use Cases**<br/>
* **Long Forms:** Breaking a massive data entry form into logical, bite-sized steps (e.g., "Personal Info," "Shipping Address," and "Payment Details").
* **Dashboards:** Hiding advanced filters or secondary analytics charts that aren't needed for the primary view.
* **Mobile Interfaces:** Maximizing vertical space on smaller screens by collapsing non-essential sections of an application.
* **Contextual Help:** Providing detailed instructions or documentation that can be hidden once the user has finished reading.`,
    helpRenders,
  };
};

export default AccordionManager;
